import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Handyman as HandymanIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../../common/ScrollableDialog';
import PrimaryActionButton from '../../common/PrimaryActionButton';
import ConfirmDialog from '../../common/ConfirmDialog';
import FixFailedImportRowsDialog from '../../data-import/FixFailedImportRowsDialog';
import MergeDuplicateCustomersDialog from '../../data-import/MergeDuplicateCustomersDialog';
import StoreCustomerService from '../../../services/storeCustomerService';
import {
  applyCustomerImportMapping,
  applyVcfCustomerRecords,
  buildInitialDuplicateDecisions,
  buildStoreCustomersImportPayload,
  findCustomerDuplicateGroups,
  parseCustomerImportFile,
  suggestCustomerImportMappings,
} from '../../../utils/customerFileImport';
import { buildAutomaticMergeDecisions } from '../../../utils/freshaCustomerCsv';
import CustomerImportMappingBoard from './CustomerImportMappingBoard';
import CustomerImportPreviewList from './CustomerImportPreviewList';
import { BRAND, brandAlpha } from '../../../theme/brandTokens';

const STEP = {
  PICK: 'pick',
  MAP: 'map',
  REVIEW: 'review',
};

const uploadButtonSx = {
  borderColor: BRAND.accent,
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  height: 40,
  px: 2,
  '&:hover': { borderColor: BRAND.accentDark, backgroundColor: brandAlpha('accent', 0.08) },
};

const textActionSx = {
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  px: 0.5,
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    color: BRAND.accentDark,
    textDecoration: 'underline',
  },
};

const ImportCustomersFromFileDialog = ({
  open,
  onClose,
  storeId,
  onImported,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(STEP.PICK);
  const [fileName, setFileName] = useState('');
  const [sourceFields, setSourceFields] = useState([]);
  const [records, setRecords] = useState([]);
  const [initialMappings, setInitialMappings] = useState([]);
  const [parseErrorKey, setParseErrorKey] = useState(null);
  const [rows, setRows] = useState([]);
  const [fixOpen, setFixOpen] = useState(false);
  const [importReadyConfirmOpen, setImportReadyConfirmOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeGroups, setMergeGroups] = useState([]);
  const [mergeDecisions, setMergeDecisions] = useState({});
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);

  const failedRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);
  const readyRows = useMemo(() => rows.filter((row) => row.errors.length === 0), [rows]);
  const warningCount = useMemo(
    () => rows.reduce((total, row) => total + (row.warnings?.length || 0), 0),
    [rows],
  );

  const resetState = () => {
    setStep(STEP.PICK);
    setFileName('');
    setSourceFields([]);
    setRecords([]);
    setInitialMappings([]);
    setParseErrorKey(null);
    setRows([]);
    setFixOpen(false);
    setImportReadyConfirmOpen(false);
    setMergeOpen(false);
    setMergeGroups([]);
    setMergeDecisions({});
    setImporting(false);
    setImportError(null);
  };

  const handleClose = () => {
    if (importing) return;
    resetState();
    onClose();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const extension = file.name.split('.').pop()?.toLowerCase();
    if (extension && extension !== 'csv' && extension !== 'vcf') {
      setParseErrorKey('customersManagement.import.errors.unsupportedType');
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCustomerImportFile(text, file.name);
      if (parsed.errorKey) {
        setParseErrorKey(parsed.errorKey);
        setSourceFields([]);
        setRecords([]);
        setFileName('');
        return;
      }

      setParseErrorKey(null);
      setFileName(file.name);
      setSourceFields(parsed.sourceFields);
      setRecords(parsed.records);
      setImportError(null);

      if (parsed.format === 'vcf') {
        setInitialMappings([]);
        setRows(applyVcfCustomerRecords(parsed.records));
        setStep(STEP.REVIEW);
        return;
      }

      setInitialMappings(suggestCustomerImportMappings(parsed.sourceFields));
      setStep(STEP.MAP);
    } catch {
      setParseErrorKey('customersManagement.import.errors.parseFailed');
    }
  };

  const handleMappingContinue = (mappings) => {
    const mappedRows = applyCustomerImportMapping(records, mappings);
    setRows(mappedRows);
    setImportError(null);
    setStep(STEP.REVIEW);
  };

  const handleSaveFixedRow = (updatedRow) => {
    setRows((current) => current.map((row) => (row.id === updatedRow.id ? updatedRow : row)));
  };

  const runImport = async (decisions) => {
    setImporting(true);
    setImportError(null);
    try {
      const payload = buildStoreCustomersImportPayload(rows, decisions);
      if (!payload.customers.length) {
        setImportError(t('customersManagement.import.errors.noRows'));
        return;
      }
      const result = await StoreCustomerService.importStoreCustomers(payload, storeId);
      resetState();
      onImported?.(result);
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.title
        || t('customersManagement.import.errors.importFailed');
      setImportError(message);
    } finally {
      setImporting(false);
      setMergeOpen(false);
      setImportReadyConfirmOpen(false);
    }
  };

  const startImportFlow = async () => {
    if (!readyRows.length || importing) return;

    const groups = findCustomerDuplicateGroups(rows);
    if (groups.length > 0) {
      setMergeGroups(groups);
      setMergeDecisions(buildInitialDuplicateDecisions(groups));
      setMergeOpen(true);
      return;
    }

    await runImport({});
  };

  const handleAutoResolveDuplicates = async () => {
    if (!readyRows.length || importing) return;
    const decisions = buildAutomaticMergeDecisions(rows);
    await runImport(decisions);
  };

  return (
    <>
      <ScrollableDialog
        open={open && step !== STEP.MAP}
        onClose={handleClose}
        maxWidth={step === STEP.REVIEW ? 'lg' : 'md'}
        title={t('customersManagement.import.title')}
        subtitle={step === STEP.PICK
          ? t('customersManagement.import.pickSubtitle')
          : undefined}
        dividers
        showCloseButton
        titleSx={{ pb: 1, flexShrink: 0 }}
        actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
        actions={(
          <>
            <Button color="inherit" onClick={handleClose} disabled={importing}>
              {t('common.cancel')}
            </Button>
            {step === STEP.REVIEW && failedRows.length === 0 ? (
              <PrimaryActionButton
                onClick={startImportFlow}
                disabled={importing || readyRows.length === 0}
              >
                {importing ? t('dataImport.customers.importing') : t('dataImport.customers.importButton')}
              </PrimaryActionButton>
            ) : (
              <Box />
            )}
          </>
        )}
      >
        {step === STEP.PICK ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={uploadButtonSx}
            >
              {t('customersManagement.import.uploadButton')}
              <input
                hidden
                type="file"
                accept=".csv,.vcf,text/csv,text/vcard,text/x-vcard"
                onChange={handleFileChange}
              />
            </Button>
            {parseErrorKey ? <Alert severity="error">{t(parseErrorKey)}</Alert> : null}
          </Box>
        ) : null}

        {step === STEP.REVIEW ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {fileName ? (
              <Typography variant="body2">
                {t('customersManagement.import.selectedFile', { fileName })}
              </Typography>
            ) : null}
            {importError ? <Alert severity="error">{importError}</Alert> : null}
            {rows.length > 0 ? (
              <Alert severity={failedRows.length ? 'warning' : 'success'}>
                {t('dataImport.customers.summary', {
                  total: rows.length,
                  ready: readyRows.length,
                  failed: failedRows.length,
                  warnings: warningCount,
                })}
              </Alert>
            ) : null}

            {failedRows.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t('dataImport.customers.failedTitle')}
                </Typography>
                {failedRows.slice(0, 8).map((row) => (
                  <Typography key={row.id} variant="body2">
                    #{row.rowNumber}: {row.errors.map((error) => t(error.key)).join(', ')}
                  </Typography>
                ))}
                {failedRows.length > 8 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('dataImport.customers.moreFailures', { count: failedRows.length - 8 })}
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mt: 0.5 }}>
                  <Button
                    variant="text"
                    startIcon={<HandymanIcon />}
                    onClick={() => setFixOpen(true)}
                    sx={textActionSx}
                  >
                    {t('dataImport.customers.fixFailingRows')}
                  </Button>
                  <PrimaryActionButton
                    startIcon={<CheckCircleIcon />}
                    onClick={() => setImportReadyConfirmOpen(true)}
                    disabled={!readyRows.length || importing}
                  >
                    {t('dataImport.customers.importReadyRows')}
                  </PrimaryActionButton>
                </Box>
              </Box>
            ) : null}

            {warningCount > 0 ? (
              <Alert severity="info">
                {t('dataImport.customers.warningsNotice', { count: warningCount })}
              </Alert>
            ) : null}

            <CustomerImportPreviewList rows={rows} />
          </Box>
        ) : null}
      </ScrollableDialog>

      {open && step === STEP.MAP ? (
        <CustomerImportMappingBoard
          fileName={fileName}
          sourceFields={sourceFields}
          initialMappings={initialMappings}
          onCancel={handleClose}
          onContinue={handleMappingContinue}
        />
      ) : null}

      <FixFailedImportRowsDialog
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        rows={failedRows}
        onSaveRow={handleSaveFixedRow}
      />

      <ConfirmDialog
        open={importReadyConfirmOpen}
        onClose={() => setImportReadyConfirmOpen(false)}
        onConfirm={startImportFlow}
        title={t('dataImport.customers.importReadyConfirmTitle')}
        message={t('dataImport.customers.importReadyConfirmMessage', {
          failed: failedRows.length,
          ready: readyRows.length,
        })}
        confirmText={t('dataImport.customers.importReadyConfirmAction')}
        confirmColor="primary"
        loading={importing}
      />

      <MergeDuplicateCustomersDialog
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        groups={mergeGroups}
        decisions={mergeDecisions}
        onChangeDecision={(groupId, decision) => {
          setMergeDecisions((current) => ({ ...current, [groupId]: decision }));
        }}
        onConfirm={() => runImport(mergeDecisions)}
        onAutoResolve={handleAutoResolveDuplicates}
        importing={importing}
      />
    </>
  );
};

export default ImportCustomersFromFileDialog;
